import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { Reveal } from "./shared";
import { PACKAGES } from "./Pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookingForm = ({ selectedPackage }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", package: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (selectedPackage) setForm((f) => ({ ...f, package: selectedPackage }));
  }, [selectedPackage]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent — we'll be in touch within one working day.");
      setForm({ name: "", email: "", phone: "", company: "", package: "", message: "" });
    } catch {
      toast.error("Something went wrong — please try emailing us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="book" className="border-t border-[#E7E3D9] bg-white px-4 py-24 sm:px-6 lg:px-8" data-testid="booking-section">
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <h2 className="font-display text-3xl font-bold tracking-tight text-[#14231B] sm:text-4xl lg:text-5xl">
            Give your team the confidence to <span className="text-[#1E7B4F]">save a life</span>
          </h2>
          <p className="mt-6 max-w-md text-lg leading-relaxed text-[#5F6E63]">
            Tell us a little about your team and we'll come back within one working day with
            available dates. Half-day and full-day visits across the UK.
          </p>
          <div className="mt-12 space-y-5 text-base">
            <a href="mailto:hello@desktodefib.co.uk" data-testid="contact-email-link" className="flex items-center gap-4 font-medium text-[#14231B] hover:text-[#1E7B4F] transition-colors duration-300">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1E7B4F]/10">
                <Mail className="h-5 w-5 text-[#1E7B4F]" />
              </span>
              hello@desktodefib.co.uk
            </a>
            <a href="tel:+442080641252" data-testid="contact-phone-link" className="flex items-center gap-4 font-medium text-[#14231B] hover:text-[#1E7B4F] transition-colors duration-300">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1E7B4F]/10">
                <Phone className="h-5 w-5 text-[#1E7B4F]" />
              </span>
              +44 (0)20 8064 1252
            </a>
            <p className="flex items-center gap-4 text-[#5F6E63]">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1E7B4F]/10">
                <MapPin className="h-5 w-5 text-[#1E7B4F]" />
              </span>
              United Kingdom — we come to you
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <form onSubmit={submit} className="rounded-3xl border border-[#E7E3D9] bg-[#F8F7F4] p-8 shadow-sm" data-testid="booking-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="booking-name" className="mb-2 block text-sm font-semibold text-[#14231B]">Name *</label>
                <input id="booking-name" data-testid="booking-name-input" required className="field-input" placeholder="Jane Smith" value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label htmlFor="booking-email" className="mb-2 block text-sm font-semibold text-[#14231B]">Work email *</label>
                <input id="booking-email" data-testid="booking-email-input" required type="email" className="field-input" placeholder="jane@company.com" value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label htmlFor="booking-phone" className="mb-2 block text-sm font-semibold text-[#14231B]">Phone</label>
                <input id="booking-phone" data-testid="booking-phone-input" className="field-input" placeholder="+44 ..." value={form.phone} onChange={set("phone")} />
              </div>
              <div>
                <label htmlFor="booking-company" className="mb-2 block text-sm font-semibold text-[#14231B]">Company</label>
                <input id="booking-company" data-testid="booking-company-input" className="field-input" placeholder="Company Ltd" value={form.company} onChange={set("company")} />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="booking-package" className="mb-2 block text-sm font-semibold text-[#14231B]">Package</label>
              <select id="booking-package" data-testid="booking-package-select" className="field-input" value={form.package} onChange={set("package")}>
                <option value="">Not sure yet</option>
                {PACKAGES.map((p) => (
                  <option key={p.id} value={p.id}>{p.id}</option>
                ))}
              </select>
            </div>
            <div className="mt-5">
              <label htmlFor="booking-message" className="mb-2 block text-sm font-semibold text-[#14231B]">Message</label>
              <textarea id="booking-message" data-testid="booking-message-input" rows={4} className="field-input resize-none" placeholder="Team size, preferred dates, anything else..." value={form.message} onChange={set("message")} />
            </div>
            <button
              type="submit"
              data-testid="booking-submit-btn"
              disabled={sending}
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1E7B4F] py-4 text-base font-semibold text-white shadow-md hover:bg-[#16603D] transition-colors duration-300 disabled:opacity-50"
            >
              {sending ? "Sending..." : "Send enquiry"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
